import { generateIdToast, toast } from "@src/utils/toast";

import { ShowCustomToastProps } from "../../types";
import { CustomToast } from "../..";

/**
 * Show a custom toast with the given props.
 * @param id - Optional id for the toast. If not provided, a random id will be generated.
 * @param type - The type of the toast.
 * @param message - The message to display in the toast.
 * @param description - The description to display in the toast.
 * @param duration - The duration of the toast.
 * @param progress - Whether to show a progress bar in the toast.
 * @param closeButton - Whether to show a close button in the toast.
 * @param dismissible - Whether the toast can be dismissed by swiping.
 * @param actions - The actions to display in the toast.
 * @returns The id of the toast.
 * @example
 * showCustomToast({
 *   id: "my-toast",
 *   type: "success",
 *   message: "Success",
 *   description: "This is a success toast",
 *   duration: 4000,
 *   progress: false,
 *   closeButton: false,
 *   dismissible: true,
 *   actions: {
 *     actionLabel: "Action",
 *     onAction: () => {
 *       console.log("Action");
 *     },
 *   },
 * });
 */
export function showCustomToast({
  id,
  type,
  message,
  description,
  duration = 4000,
  progress = true,
  closeButton = false,
  actions,
  dismissible = true,
}: ShowCustomToastProps) {
  const customToastId = generateIdToast();
  return toast.custom(
    () => (
      <CustomToast
        id={id ?? customToastId}
        type={type}
        message={message}
        description={description}
        actions={actions}
        closeButton={closeButton}
        duration={duration}
        progress={progress}
        dismissible={dismissible}
      />
    ),
    {
      id: id ?? customToastId,
      duration: duration,
      dismissible: dismissible,
    },
  );
}
